import React, { useEffect, useState } from 'react';
import moment from 'moment';
import '../styles/home.style.scss';
import { RouteEnum } from 'src/_shared/routes';
import { store } from 'src/_shared/store/configure';
import { useStoreSelector } from 'src/_shared/store/hook';
import { setCurrentRoute } from 'src/features/_shared/store/sharedSlices';
import { NavigationManager } from 'src/_shared/managers/navigation.manager';
import { DashboardManager } from 'src/_shared/managers/dashboard.manager';
import { UserManager } from 'src/_shared/managers/user.manager';
import { ExerciseManager } from 'src/_shared/managers/exercise.manager';
import { QueueManager, QueueKeyType } from 'src/_shared/managers/queue.manager';
import { User, Exercise, DashboardHomeResponse, MostTrainedExercise, HomeMuscleGroupRanking, DashboardTimelineSummary } from 'src/api/entities';
import { Doughnut, HorizontalBar } from 'react-chartjs-2';
import { PageTransition } from 'src/features/_shared/components/PageTransition/page.transition.component';
import { MOCK_SALES, getSalesTotals } from 'src/features/sales/mocks/sales.mock';
import { Button } from 'src/features/_shared/components/Button/button.component';
import { setCustomerItem, setCustomerItems } from 'src/features/customer/state/customer.slice';

moment.locale('it');

const groupColors: Record<string, string> = {
    CHEST: '#ff4949', BACK: '#D93955', QUADRICEPS: '#e06040', TRICEPS: '#c74a72',
    GLUTEUS: '#b05ca6', SHOULDERS: '#7b68c9', HARMSTRINGS: '#4a8fd9', BICEPS: '#ff6b6b',
    ABS: '#e84393', CALVES: '#e17055', TRAPS: '#a29bfe', ADDUCTORS: '#6c5ce7',
    ABDUCTORS: '#fd79a8', NECK: '#74b9ff',
};

const groupLabels: Record<string, string> = {
    CHEST: 'Petto', BACK: 'Schiena', QUADRICEPS: 'Quadricipiti', TRICEPS: 'Tricipiti',
    GLUTEUS: 'Glutei', SHOULDERS: 'Spalle', HARMSTRINGS: 'Femorali', BICEPS: 'Bicipiti',
    ABS: 'Addominali', CALVES: 'Polpacci', TRAPS: 'Trapezio', ADDUCTORS: 'Adduttori',
    ABDUCTORS: 'Abduttori', NECK: 'Collo',
};

const avatarColors = ['#ff4949', '#D93955', '#e06040', '#c74a72', '#b05ca6', '#7b68c9', '#4a8fd9'];

export const HomeView: React.FC = () => {
    const navigationManager = NavigationManager.getInstance();
    const dashboardManager = DashboardManager.getInstance();
    const userManager = UserManager.getInstance();
    const exerciseManager = ExerciseManager.getInstance();
    const queueManager = QueueManager.getInstance();

    const user: User = useStoreSelector(state => state.reducer.accessSlice.loggedUser);
    const customers: User[] = useStoreSelector(state => state.reducer.customerSlice.customers);

    const [dashboardHome, setDashboardHome] = useState<DashboardHomeResponse | null>(null);
    const [clientList, setClientList] = useState<User[]>([]);
    const [exerciseCount, setExerciseCount] = useState<number | null>(null);

    useEffect(() => {
        store.dispatch(setCurrentRoute(RouteEnum.home));
        window.scrollTo(0, 0);

        (async () => {
            try {
                const users = (await userManager.search()).user ?? [];
                store.dispatch(setCustomerItems({ value: users }));
                setClientList(users);
            } catch { /* ignore */ }

            try {
                const home = await dashboardManager.getDashboardHome({
                    authorID: user.id,
                    year: new Date().getFullYear(),
                });
                setDashboardHome(home);
            } catch { /* ignore */ }

            try {
                const exercises = await exerciseManager.search(user.id);
                setExerciseCount(exercises.length);
            } catch { /* ignore */ }
        })();
    }, []);

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Buongiorno';
        if (hour < 18) return 'Buon pomeriggio';
        return 'Buonasera';
    };

    const firstName = user.data?.details?.firstName ?? user.username ?? '';

    const exercisesRanking = dashboardHome?.exercisesRanking ?? [];
    const rankingItems = exercisesRanking.slice(0, 8);
    const rankingChartHeight = 28 * rankingItems.length + 30;
    const muscleGroups = dashboardHome?.muscleGroupComposition ?? [];
    const timeline = dashboardHome?.timelineSummary ?? [];
    const frequency = dashboardHome?.frequency?.frequency ?? 0;
    const monthlyFrequency = dashboardHome?.monthlyFrequency?.frequency ?? 0;

    const getStatusColor = (status?: string) => {
        switch (status) {
            case 'DONE': return '#1a7f42';
            case 'STARTED': return '#b8860b';
            case 'CANCELLED': return '#b91c1c';
            default: return '#aaa';
        }
    };

    const getInitials = (c: User) => {
        const first = c.data?.details?.firstName?.[0] ?? '';
        const last = c.data?.details?.lastName?.[0] ?? '';
        return (first + last).toUpperCase() || '?';
    };

    const getAvatarColor = (c: User) => avatarColors[(c.id ?? 0) % avatarColors.length];

    const handleGoToClient = async (client: User) => {
        const value: User = await queueManager.queue(QueueKeyType.DEFAULT, userManager.getUserById(client.id!));
        store.dispatch(setCustomerItem({ value }));
        navigationManager.navigateTo(RouteEnum.customersWorkouts);
    };

    const handleGoToClientSessions = async (client: User) => {
        const value: User = await queueManager.queue(QueueKeyType.DEFAULT, userManager.getUserById(client.id!));
        store.dispatch(setCustomerItem({ value }));
        navigationManager.navigateTo(RouteEnum.customersSessions);
    };

    const salesTotals = getSalesTotals(MOCK_SALES);

    // Stats for PT
    const statCards = [
        { label: 'Clienti attivi', icon: 'bi-people-fill', value: clientList.filter(c => c.active).length, total: clientList.length, color: '#ff4949', route: RouteEnum.customers },
        { label: 'Sedute questo mese', icon: 'bi-calendar-check-fill', value: monthlyFrequency, total: null, color: '#1a7f42', route: null },
        { label: 'Sedute quest\'anno', icon: 'bi-graph-up-arrow', value: frequency, total: null, color: '#4a8fd9', route: null },
        { label: 'Catalogo esercizi', icon: 'bi-activity', value: exerciseCount, total: null, color: '#7b68c9', route: RouteEnum.exercises },
        { label: 'Vendite totali', icon: 'bi-cart-fill', value: salesTotals.count, total: null, color: '#e06040', route: RouteEnum.sales },
        { label: 'Ricavo totale', icon: 'bi-currency-euro', value: '\u20AC' + salesTotals.revenue, total: null, color: '#1a7f42', route: RouteEnum.sales },
    ];

    return (
        <PageTransition className='mt-4 px-3 px-md-4'>
            {/* ── Hero Greeting ───────────────────────────────────────── */}
            <div className='app-card mb-4 anim-slide-up' style={{ padding: '22px 24px', borderLeft: '3px solid #ff4949' }}>
                <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between'>
                    <div className='d-flex align-items-center gap-3'>
                        <div style={{
                            width: 46, height: 46, borderRadius: 12,
                            background: 'linear-gradient(135deg, #ff4949, #D93955)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 3px 12px rgba(255,73,73,0.2)',
                            color: '#fff', fontSize: '1.15rem', fontWeight: 700,
                        }}>
                            {(firstName[0] ?? '?').toUpperCase()}
                        </div>
                        <div>
                            <h3 style={{ margin: 0, color: '#222', fontWeight: 700, fontSize: '1.25rem', letterSpacing: '-0.02em' }}>
                                {getGreeting()}, {firstName}
                            </h3>
                            <p style={{ margin: 0, color: '#aaa', fontSize: '0.8rem' }}>
                                {moment().format('dddd D MMMM YYYY')} &middot; Il tuo pannello di controllo
                            </p>
                        </div>
                    </div>
                    <div className='d-flex gap-2 mt-3 mt-md-0'>
                        <Button
                            identifier='Clienti'
                            icon={<i className='bi bi-people-fill' style={{ fontSize: '0.9rem', color: 'white' }}></i>}
                            variant='primary'
                            onClick={() => navigationManager.navigateTo(RouteEnum.customers)}
                        />
                        <Button
                            identifier='Esercizi'
                            icon={<i className='bi bi-activity' style={{ fontSize: '0.9rem', color: 'white' }}></i>}
                            variant='primary'
                            onClick={() => navigationManager.navigateTo(RouteEnum.exercises)}
                        />
                    </div>
                </div>
            </div>

            {/* ── Stat Cards ──────────────────────────────────────────── */}
            <div className='row g-3 mb-4'>
                {statCards.map((stat, index) => (
                    <div className='col-6 col-lg-3' key={stat.label}>
                        <div
                            className='app-card anim-stagger'
                            style={{
                                '--anim-index': index,
                                cursor: stat.route ? 'pointer' : 'default',
                                padding: '18px',
                                borderTop: `3px solid ${stat.color}`,
                            } as React.CSSProperties}
                            onClick={() => stat.route && navigationManager.navigateTo(stat.route as any)}
                        >
                            <div className='d-flex align-items-center gap-2 mb-2'>
                                <div style={{
                                    width: 32, height: 32, borderRadius: 8,
                                    backgroundColor: `${stat.color}12`, color: stat.color,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.95rem',
                                }}>
                                    <i className={`bi ${stat.icon}`}></i>
                                </div>
                                <span style={{ color: '#888', fontSize: '0.75rem', fontWeight: 500 }}>{stat.label}</span>
                            </div>
                            <div className='d-flex align-items-baseline gap-2'>
                                {stat.value !== null ? (
                                    <span style={{ fontSize: '1.6rem', fontWeight: 700, color: '#222', lineHeight: 1 }}>
                                        {stat.value}
                                    </span>
                                ) : (
                                    <span style={{ fontSize: '0.82rem', color: '#aaa' }}>Vai</span>
                                )}
                                {stat.total !== null && (
                                    <span style={{ fontSize: '0.75rem', color: '#bbb' }}>/ {stat.total}</span>
                                )}
                                {stat.route && (
                                    <i className='bi bi-arrow-right ms-auto' style={{ color: '#ddd', fontSize: '0.85rem' }}></i>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Client Roster ────────────────────────────────────────── */}
            <div className='app-card anim-slide-up mb-4' style={{ padding: '20px' }}>
                <div className='d-flex align-items-center justify-content-between mb-3'>
                    <div className='d-flex align-items-center gap-2'>
                        <i className='bi bi-people-fill' style={{ fontSize: '1rem', color: '#ff4949' }}></i>
                        <h6 className='mb-0 fw-semibold' style={{ color: '#333', fontSize: '0.95rem' }}>I tuoi clienti</h6>
                        {clientList.length > 0 && (
                            <span className='badge rounded-pill' style={{ backgroundColor: '#f2f2f2', color: '#666', fontSize: '0.7rem' }}>
                                {clientList.length}
                            </span>
                        )}
                    </div>
                    <Button
                        identifier='Vedi tutti'
                        variant='secondary'
                        onClick={() => navigationManager.navigateTo(RouteEnum.customers)}
                    />
                </div>

                {clientList.length > 0 ? (
                    <div className='d-flex flex-wrap gap-2'>
                        {clientList.slice(0, 12).map((client, index) => (
                            <div
                                key={client.id || index}
                                className='d-flex align-items-center gap-2 anim-stagger'
                                style={{
                                    '--anim-index': index,
                                    padding: '8px 14px 8px 8px',
                                    borderRadius: 50,
                                    backgroundColor: '#fafafa',
                                    border: '1px solid rgba(0,0,0,0.05)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                } as React.CSSProperties}
                                onClick={() => handleGoToClient(client)}
                                onMouseEnter={e => {
                                    (e.currentTarget as HTMLElement).style.backgroundColor = '#f2f2f2';
                                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,0,0,0.1)';
                                }}
                                onMouseLeave={e => {
                                    (e.currentTarget as HTMLElement).style.backgroundColor = '#fafafa';
                                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,0,0,0.05)';
                                }}
                            >
                                <div style={{
                                    width: 28, height: 28, borderRadius: '50%',
                                    backgroundColor: getAvatarColor(client), color: '#fff',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.65rem', fontWeight: 700, flexShrink: 0,
                                }}>
                                    {getInitials(client)}
                                </div>
                                <span style={{ fontSize: '0.82rem', color: '#333', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                    {client.data?.details?.firstName} {client.data?.details?.lastName}
                                </span>
                                {client.active === false && (
                                    <span style={{
                                        width: 6, height: 6, borderRadius: '50%',
                                        backgroundColor: '#ddd', flexShrink: 0,
                                    }} />
                                )}
                            </div>
                        ))}
                        {clientList.length > 12 && (
                            <div
                                className='d-flex align-items-center justify-content-center'
                                style={{
                                    padding: '8px 16px', borderRadius: 50,
                                    backgroundColor: '#f2f2f2', color: '#888',
                                    fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer',
                                }}
                                onClick={() => navigationManager.navigateTo(RouteEnum.customers)}
                            >
                                +{clientList.length - 12} altri
                            </div>
                        )}
                    </div>
                ) : (
                    <div className='d-flex align-items-center justify-content-center' style={{ height: 80, color: '#ccc', fontSize: '0.85rem' }}>
                        <i className='bi bi-inbox me-2'></i>Nessun cliente registrato
                    </div>
                )}
            </div>

            <div className='row g-3 mb-4'>
                {/* ── Esercizi pi&ugrave; prescritti ──────────────────────── */}
                <div className='col-12 col-lg-7'>
                    <div className='app-card anim-slide-up' style={{ padding: '20px', height: '100%' }}>
                        <div className='d-flex align-items-center gap-2 mb-3'>
                            <i className='bi bi-clipboard2-pulse-fill' style={{ fontSize: '1rem', color: '#ff4949' }}></i>
                            <h6 className='mb-0 fw-semibold' style={{ color: '#333', fontSize: '0.95rem' }}>Esercizi pi&ugrave; prescritti</h6>
                            <span className='badge rounded-pill' style={{ backgroundColor: '#f2f2f2', color: '#666', fontSize: '0.7rem' }}>
                                {new Date().getFullYear()}
                            </span>
                        </div>

                        {rankingItems.length > 0 ? (
                            <div style={{ height: rankingChartHeight }}>
                                <HorizontalBar
                                    data={{
                                        labels: rankingItems.map(e => e.exerciseName ?? ''),
                                        datasets: [{
                                            label: 'Frequenza',
                                            data: rankingItems.map(e => e.frequency ?? 0),
                                            backgroundColor: rankingItems.map(e => {
                                                const c = groupColors[e.exerciseGroup ?? ''] ?? '#ff4949';
                                                return c + '30';
                                            }),
                                            borderColor: rankingItems.map(e =>
                                                groupColors[e.exerciseGroup ?? ''] ?? '#ff4949'
                                            ),
                                            borderWidth: 1.5,
                                            barPercentage: 0.6,
                                            categoryPercentage: 0.7,
                                        }],
                                    }}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        legend: { display: false },
                                        scales: {
                                            xAxes: [{
                                                ticks: { beginAtZero: true, fontSize: 10, fontColor: '#bbb', precision: 0, maxTicksLimit: 5 },
                                                gridLines: { color: 'rgba(0,0,0,0.03)', zeroLineColor: 'rgba(0,0,0,0.05)' },
                                            }],
                                            yAxes: [{
                                                ticks: { fontSize: 11, fontColor: '#555', padding: 4 },
                                                gridLines: { display: false },
                                            }],
                                        },
                                        tooltips: {
                                            backgroundColor: '#222',
                                            titleFontSize: 11,
                                            bodyFontSize: 11,
                                            cornerRadius: 6,
                                            callbacks: {
                                                label: (item: any) => ` ${item.xLabel}x prescritto`,
                                            },
                                        },
                                    }}
                                />
                            </div>
                        ) : (
                            <div className='d-flex align-items-center justify-content-center' style={{ height: 120, color: '#ccc', fontSize: '0.85rem' }}>
                                <i className='bi bi-inbox me-2'></i>Nessun dato disponibile
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Distribuzione muscolare ─────────────────────────── */}
                <div className='col-12 col-lg-5'>
                    <div className='app-card anim-slide-up' style={{ padding: '20px', height: '100%' }}>
                        <div className='d-flex align-items-center gap-2 mb-3'>
                            <i className='bi bi-pie-chart-fill' style={{ fontSize: '1rem', color: '#7b68c9' }}></i>
                            <h6 className='mb-0 fw-semibold' style={{ color: '#333', fontSize: '0.95rem' }}>Distribuzione muscolare</h6>
                        </div>
                        <p style={{ margin: '0 0 12px', color: '#aaa', fontSize: '0.75rem' }}>
                            Come stai distribuendo il lavoro tra i clienti
                        </p>

                        {muscleGroups.length > 0 ? (
                            <div className='d-flex flex-column align-items-center'>
                                <div style={{ width: '100%', maxWidth: 260, margin: '0 auto' }}>
                                    <Doughnut
                                        data={{
                                            labels: muscleGroups.slice(0, 8).map(g =>
                                                groupLabels[g.exerciseGroup ?? ''] ?? g.exerciseGroup
                                            ),
                                            datasets: [{
                                                data: muscleGroups.slice(0, 8).map(g =>
                                                    parseFloat(g.percentage ?? '0')
                                                ),
                                                backgroundColor: muscleGroups.slice(0, 8).map(g =>
                                                    groupColors[g.exerciseGroup ?? ''] ?? '#aaa'
                                                ),
                                                borderColor: '#fff',
                                                borderWidth: 2,
                                                hoverBorderColor: '#fff',
                                                hoverBorderWidth: 3,
                                            }],
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: true,
                                            cutoutPercentage: 62,
                                            legend: { display: false },
                                            tooltips: {
                                                backgroundColor: '#222',
                                                titleFontSize: 12,
                                                bodyFontSize: 12,
                                                cornerRadius: 6,
                                                callbacks: {
                                                    label: (item: any, data: any) => {
                                                        const label = data.labels[item.index] ?? '';
                                                        const value = data.datasets[0].data[item.index] ?? 0;
                                                        return ` ${label}: ${parseFloat(value).toFixed(0)}%`;
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </div>
                                <div className='d-flex flex-wrap justify-content-center gap-2 mt-3'>
                                    {muscleGroups.slice(0, 8).map((group) => {
                                        const color = groupColors[group.exerciseGroup ?? ''] ?? '#aaa';
                                        return (
                                            <div key={group.exerciseGroup} className='d-flex align-items-center gap-1'>
                                                <div style={{
                                                    width: 8, height: 8, borderRadius: '50%',
                                                    backgroundColor: color, flexShrink: 0,
                                                }} />
                                                <span style={{ fontSize: '0.7rem', color: '#666' }}>
                                                    {groupLabels[group.exerciseGroup ?? ''] ?? group.exerciseGroup}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div className='d-flex align-items-center justify-content-center' style={{ height: 120, color: '#ccc', fontSize: '0.85rem' }}>
                                <i className='bi bi-inbox me-2'></i>Nessun dato disponibile
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Sedute recenti dei clienti ───────────────────────────── */}
            <div className='app-card anim-slide-up mb-4' style={{ padding: '20px' }}>
                <div className='d-flex align-items-center justify-content-between mb-3'>
                    <div className='d-flex align-items-center gap-2'>
                        <i className='bi bi-clock-history' style={{ fontSize: '1rem', color: '#e06040' }}></i>
                        <h6 className='mb-0 fw-semibold' style={{ color: '#333', fontSize: '0.95rem' }}>Sedute recenti dei clienti</h6>
                        {timeline.length > 0 && (
                            <span className='badge rounded-pill' style={{ backgroundColor: '#f2f2f2', color: '#666', fontSize: '0.7rem' }}>
                                {timeline.length}
                            </span>
                        )}
                    </div>
                </div>

                {timeline.length > 0 ? (
                    <div className='d-flex flex-column' style={{ position: 'relative', paddingLeft: 24 }}>
                        <div style={{
                            position: 'absolute', left: 7, top: 4, bottom: 4, width: 2,
                            background: 'linear-gradient(180deg, #f2f2f2 0%, rgba(242,242,242,0) 100%)',
                            borderRadius: 1,
                        }} />

                        {timeline.slice(0, 15).map((entry, index) => (
                            <div key={entry.id || index} className='d-flex align-items-start gap-3 mb-3' style={{ position: 'relative' }}>
                                <div style={{
                                    position: 'absolute', left: -20, top: 5,
                                    width: 10, height: 10, borderRadius: '50%',
                                    backgroundColor: getStatusColor(entry.status),
                                    border: '2px solid #fff',
                                    boxShadow: `0 0 0 2px ${getStatusColor(entry.status)}25`,
                                    flexShrink: 0,
                                }} />

                                <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center gap-1 gap-md-3' style={{ flex: 1 }}>
                                    <span style={{ fontSize: '0.75rem', color: '#aaa', minWidth: 85 }}>
                                        {entry.day ? moment(entry.day).format('D MMM YYYY') : '—'}
                                    </span>
                                    <span style={{ fontSize: '0.85rem', color: '#333', fontWeight: 500 }}>
                                        {entry.sessionName ?? 'Seduta'}
                                    </span>
                                    <span
                                        className='badge rounded-pill'
                                        style={{
                                            backgroundColor: `${getStatusColor(entry.status)}15`,
                                            color: getStatusColor(entry.status),
                                            fontSize: '0.65rem',
                                            fontWeight: 500,
                                        }}
                                    >
                                        {entry.status === 'DONE' ? 'Completata' : entry.status === 'STARTED' ? 'In corso' : entry.status === 'CANCELLED' ? 'Annullata' : entry.status}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className='d-flex align-items-center justify-content-center' style={{ height: 100, color: '#ccc', fontSize: '0.85rem' }}>
                        <i className='bi bi-inbox me-2'></i>Nessuna seduta registrata
                    </div>
                )}
            </div>
        </PageTransition>
    );
};
